import { useCallback, useMemo, useRef, useState } from "react";
import { Box, Button, IconButton, Theme, Tooltip, Typography } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import { GridColDef, GridRenderCellParams, GridValueGetterParams } from "@mui/x-data-grid";
import _ from "lodash";
import moment from "moment";
import { useDispatch } from "react-redux";
import {
  IVacationBidPackageResponse,
  IVacationBidSlot,
  IVacationBidSlotGeneration,
  IVacationDate,
} from "@vmosolutions/oc-interfaces";

// Components
import { SpinnerProgress } from "src/components/SpinnerProgress/SpinnerProgress";
import {
  EditableDataGrid,
  dataCol,
  editableBooleanColumn,
  editableIntegerColumn,
  editableIsoDateColumn,
  readOnlyTextColumn,
  useEditableGridPagination,
} from "src/components/EditableDataGrid";
import { INotifications } from "src/API/AmplifyAPIWrapper";

import { IVacationSlotsTabProps } from "../types";
import { VACATION_BID_TEST_ID } from "../testIds";
import {
  buildSlotId,
  compareVacationDates,
  dateToVacationDate,
  displaySlotId,
  isoToVacationDate,
  saveVacationBidSlots,
  vacationCsvFileName,
  vacationDateToIso,
} from "../helpers";
import { GenerateSlotsDialog } from "./GenerateSlotsDialog";
import { ImportCsvDialog } from "../Csv/ImportCsvDialog";
import { download } from "src/utils/downloadTextFile";
import { ICsvParseResult, parseCsvFile } from "src/utils/csvFile";
import { slotsFromCsv, slotsToCsv } from "../Csv/slotsCsv";
import { awardAdjustmentsLine, awardChangesSnackbar } from "../Awarding/awardingHelpers";
import { openSnackbar } from "src/redux/actions/App.Actions";

/** A grid row. Dates are `YYYY-MM-DD` strings, the form the date columns edit. */
interface ISlotRow {
  /**
   * Grid row key. The slot id, suffixed with its occurrence when the working copy holds the same
   * id more than once, so a duplicated block stays visible and can be corrected.
   */
  rowKey: string;
  slotId: string;
  startDate: string;
  endDate: string;
  /** Inclusive day count, derived from the dates. */
  capacity: number;
  include: boolean;
}

const getSlotRowId = (row: ISlotRow): string => row.rowKey;

/** Non-data column holding the row action buttons; never highlighted. */
const ACTIONS_FIELD = "actions";
/** Fields whose values can fail validation. */
const VALIDATED_FIELDS = ["slotId", "startDate", "endDate"];

const DEFAULT_SLOT_LENGTH_DAYS = 5;
const DEFAULT_CAPACITY = 24;

const LOCKED_REMOVE_TOOLTIP =
  "Crew can already see this block, so it cannot be removed. Untick Include to stop offering it.";

const VacationSlotsTabStyles = (theme: Theme): any => ({
  root: {
    display: "flex",
    flexDirection: "column",
    width: "100%",
    height: "100%",
    boxSizing: "border-box",
    padding: "10px",
    position: "relative",
    "& .slots_toolbar": {
      display: "flex",
      flexWrap: "wrap",
      alignItems: "center",
      width: "100%",
      "& .MuiButtonBase-root": { padding: "2px 8px", margin: "0px 5px 5px 0px", fontSize: 12 },
      "& .slots_toolbar_spacer": { flex: 1 },
    },
    "& .slots_caption": {
      margin: "0px 0px 5px",
      fontSize: 12,
      color: theme.custom.forms.labels,
      "& .protected_note": { color: theme.custom.forms.warningColor },
    },
    // The spinner overlays the tab rather than taking layout space and pushing the table down.
    "& .slotsSavingSpinner": {
      position: "absolute",
      top: 0,
      right: 0,
      bottom: 0,
      left: 0,
      zIndex: 10,
    },
    "& .slots_grid_container": {
      flex: 1,
      minHeight: "200px",
      minWidth: 0,
      width: "100%",
      position: "relative",
      backgroundColor: theme.palette.background.paper,
      // The grid is pinned out of flow: it contributes nothing to any ancestor's intrinsic width,
      // so its size comes only from this container and can never feed back into it.
      "& > .MuiDataGrid-root": {
        position: "absolute",
        top: 0,
        right: 0,
        bottom: 0,
        left: 0,
        border: "none",
      },
    },
  },
});

const slotRowKey = (slotId: string, occurrence: number): string =>
  occurrence === 0 ? slotId : `${slotId}#${occurrence}`;

/** Shift a calendar date by whole days. Null when the date cannot be parsed. */
const shiftVacationDate = (date: IVacationDate, days: number): IVacationDate | null =>
  isoToVacationDate(moment.utc(vacationDateToIso(date), "YYYY-MM-DD").add(days, "days").format("YYYY-MM-DD"));

/**
 * Fold an edited row back into its working slot. The slot id is rebuilt from the start date so it
 * never drifts from the dates it names; an unchanged date keeps its stored object.
 */
const rowToSlot = (row: ISlotRow, previous: IVacationBidSlot | undefined): IVacationBidSlot => {
  const startDate =
    previous && row.startDate === vacationDateToIso(previous.startDate)
      ? previous.startDate
      : isoToVacationDate(row.startDate);
  const endDate =
    previous && row.endDate === vacationDateToIso(previous.endDate) ? previous.endDate : isoToVacationDate(row.endDate);
  return {
    ...previous,
    slotId: buildSlotId(startDate),
    startDate,
    endDate,
    capacity: row.capacity,
    include: row.include,
  };
};

/**
 * Vacation Blocks tab - edits a package's slot table in a working copy and persists it as a whole
 * when the admin saves. Rows can be added one at a time or generated from a schedule.
 */
export const VacationSlotsTab = (props: IVacationSlotsTabProps): JSX.Element => {
  const { packageId, packageTitle, slots, bidBlockStart, bidBlockEnd, slotsProtected, showSlotIdYear, onSlotsSaved } =
    props;

  const dispatch = useDispatch();

  const [working, setWorking] = useState<IVacationBidSlot[]>(slots);

  // The working copy follows the saved table whenever a new one arrives.
  const [syncedSlots, setSyncedSlots] = useState<IVacationBidSlot[]>(slots);
  if (slots !== syncedSlots) {
    setSyncedSlots(slots);
    setWorking(slots);
  }

  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [generateOpen, setGenerateOpen] = useState<boolean>(false);
  // Remembered for the session so the generate dialog reopens with the last parameters used.
  const [lastGeneration, setLastGeneration] = useState<IVacationBidSlotGeneration | undefined>(undefined);

  const gridContainerRef = useRef<HTMLDivElement | null>(null);

  const savedById = useMemo(() => new Map(slots.map((slot) => [slot.slotId, slot])), [slots]);

  const blockStart = useMemo(() => dateToVacationDate(bidBlockStart), [bidBlockStart]);
  const blockEnd = useMemo(() => dateToVacationDate(bidBlockEnd), [bidBlockEnd]);

  const gridRows = useMemo<ISlotRow[]>(() => {
    const occurrences = new Map<string, number>();
    return working.map((slot) => {
      const occurrence = occurrences.get(slot.slotId) ?? 0;
      occurrences.set(slot.slotId, occurrence + 1);
      const startDate = vacationDateToIso(slot.startDate);
      const endDate = vacationDateToIso(slot.endDate);
      return {
        rowKey: slotRowKey(slot.slotId, occurrence),
        slotId: slot.slotId,
        startDate,
        endDate,
        capacity: slot.capacity,
        include: slot.include,
      };
    });
  }, [working]);

  // Rows and the working copy are built in the same order, so a row key maps straight to its slot.
  const workingByRowKey = useMemo(
    () => new Map(gridRows.map((row, index) => [row.rowKey, working[index]])),
    [gridRows, working]
  );

  const duplicateIds = useMemo(() => {
    const counts = new Map<string, number>();
    working.forEach((slot) => counts.set(slot.slotId, (counts.get(slot.slotId) ?? 0) + 1));
    return new Set(
      Array.from(counts.entries())
        .filter(([, count]) => count > 1)
        .map(([slotId]) => slotId)
    );
  }, [working]);

  const { paginationModel, onPaginationModelChange, requestFocusRow } = useEditableGridPagination({
    rows: gridRows,
    getRowId: getSlotRowId,
    containerRef: gridContainerRef,
  });

  const slotsDirty = useMemo(() => !_.isEqual(working, slots), [working, slots]);
  const includedCount = useMemo(() => working.filter((slot) => slot.include).length, [working]);

  /** A saved slot on an opened package: it may not be removed or re-dated. */
  const isLockedRow = useCallback(
    (row: ISlotRow): boolean => slotsProtected && savedById.has(row.slotId),
    [slotsProtected, savedById]
  );

  /** A cell is dirty when it differs from the saved slot with the same id; a new row is dirty everywhere. */
  const isCellDirty = useCallback(
    (row: ISlotRow, field: string): boolean => {
      if (field === ACTIONS_FIELD) return false;
      const saved = savedById.get(row.slotId);
      if (!saved) return true;
      switch (field) {
        case "startDate":
          return row.startDate !== vacationDateToIso(saved.startDate);
        case "endDate":
          return row.endDate !== vacationDateToIso(saved.endDate);
        case "capacity":
          return row.capacity !== saved.capacity;
        case "include":
          return row.include !== saved.include;
        default:
          return false;
      }
    },
    [savedById]
  );

  /**
   * A start date must fall inside the bid block and name a unique slot; an end date must not
   * precede its start. The end date may run past the bid block.
   */
  const isCellInvalid = useCallback(
    (row: ISlotRow, field: string): boolean => {
      const start = isoToVacationDate(row.startDate);
      const end = isoToVacationDate(row.endDate);
      switch (field) {
        case "slotId":
          return duplicateIds.has(row.slotId);
        case "startDate":
          if (!start) return true;
          if (blockStart && compareVacationDates(start, blockStart) < 0) return true;
          if (blockEnd && compareVacationDates(start, blockEnd) > 0) return true;
          return duplicateIds.has(row.slotId);
        case "endDate":
          return !start || !end || compareVacationDates(end, start) < 0;
        default:
          return false;
      }
    },
    [blockStart, blockEnd, duplicateIds]
  );

  const hasInvalidCells = useMemo(
    () => gridRows.some((row) => VALIDATED_FIELDS.some((field) => isCellInvalid(row, field))),
    [gridRows, isCellInvalid]
  );

  const isCellEditable = useCallback(
    (row: ISlotRow, field: string): boolean =>
      field === "startDate" || field === "endDate" ? !isLockedRow(row) : true,
    [isLockedRow]
  );

  const handleRowsChange = useCallback(
    (rows: ISlotRow[]) => {
      setWorking(rows.map((row) => rowToSlot(row, workingByRowKey.get(row.rowKey))));
    },
    [workingByRowKey]
  );

  const handleDeleteRow = useCallback(
    (rowKey: string) => {
      setWorking(gridRows.filter((row) => row.rowKey !== rowKey).map((row) => workingByRowKey.get(row.rowKey)));
    },
    [gridRows, workingByRowKey]
  );

  /** Appends a block starting the day after the last one ends, or at the bid block start when the table is empty. */
  const handleAddSlot = useCallback(() => {
    const last = working[working.length - 1];
    // Every date here can fail to parse, so each step falls back rather than reaching buildSlotId
    // with nothing to read.
    const startDate = (last ? shiftVacationDate(last.endDate, 1) : blockStart) ?? dateToVacationDate(new Date());
    if (!startDate) return;
    const endDate = shiftVacationDate(startDate, DEFAULT_SLOT_LENGTH_DAYS - 1) ?? startDate;
    const slot: IVacationBidSlot = {
      slotId: buildSlotId(startDate),
      startDate,
      endDate,
      capacity: last?.capacity ?? DEFAULT_CAPACITY,
      include: true,
    };
    const occurrence = working.filter((existing) => existing.slotId === slot.slotId).length;
    requestFocusRow(slotRowKey(slot.slotId, occurrence), true);
    setWorking([...working, slot]);
  }, [working, blockStart, requestFocusRow]);

  /**
   * Replaces the working copy with a new set of blocks, from generation or from a file.
   *
   * Protected slots keep their dates: one the incoming set reproduces takes its capacity and
   * include, and one it does not produce is carried over untouched, so nothing crew may have bid
   * on disappears.
   */
  const applySlots = useCallback(
    (incoming: IVacationBidSlot[]) => {
      const protectedById = new Map((slotsProtected ? slots : []).map((slot) => [slot.slotId, slot]));
      const incomingIds = new Set(incoming.map((slot) => slot.slotId));
      const merged: IVacationBidSlot[] = [
        ...incoming.map((slot) => {
          const kept = protectedById.get(slot.slotId);
          return kept ? { ...kept, capacity: slot.capacity, include: slot.include } : slot;
        }),
        ...Array.from(protectedById.values()).filter((slot) => !incomingIds.has(slot.slotId)),
      ];
      merged.sort((a, b) => compareVacationDates(a.startDate, b.startDate));
      setWorking(merged);
    },
    [slots, slotsProtected]
  );

  const handleGenerated = useCallback(
    (generated: IVacationBidSlot[], generation: IVacationBidSlotGeneration) => {
      setLastGeneration(generation);
      applySlots(generated);
    },
    [applySlots]
  );

  // The chosen file's parsed contents, held while the admin confirms the import.
  const [csvImport, setCsvImport] = useState<{ fileName: string; result: ICsvParseResult<IVacationBidSlot> } | null>(
    null
  );
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const handleOpenImport = useCallback(() => fileInputRef.current?.click(), []);

  const handleFileChosen = useCallback(async (event: React.ChangeEvent<HTMLInputElement>): Promise<void> => {
    const file = event.target.files?.[0];
    // Cleared so that choosing the same file twice in a row still raises a change event.
    event.target.value = "";
    if (!file) return;
    setCsvImport({ fileName: file.name, result: slotsFromCsv(await parseCsvFile(file)) });
  }, []);

  const handleCloseImport = useCallback(() => setCsvImport(null), []);

  // Exports the working copy, so what leaves matches what is on screen rather than what is stored.
  const handleExport = useCallback(() => {
    download(vacationCsvFileName(packageTitle, "blocks"), slotsToCsv(working));
  }, [packageTitle, working]);

  const handleConfirmImport = useCallback(() => {
    if (csvImport) applySlots(csvImport.result.rows);
    setCsvImport(null);
  }, [csvImport, applySlots]);

  /** What confirming the open import would do, stated before it happens. */
  const importSummary = useMemo<string[]>(() => {
    if (!csvImport) return [];
    const imported = csvImport.result.rows;
    // With nothing readable there is no import to describe, and the reported lines are the message.
    if (!imported.length) return ["No blocks could be read from this file."];
    const lines = [
      `${imported.length} block${imported.length === 1 ? "" : "s"} will replace the ${working.length} currently in the table.`,
    ];
    if (slotsProtected) {
      const importedIds = new Set(imported.map((slot) => slot.slotId));
      const carriedOver = slots.filter((slot) => !importedIds.has(slot.slotId)).length;
      if (carriedOver > 0) {
        lines.push(
          `${carriedOver} saved block${carriedOver === 1 ? "" : "s"} the file does not list will be kept, because the bid window has opened and they cannot be removed.`
        );
      }
    }
    // A file built for a different bid year parses perfectly well, so the mismatch is named here
    // rather than left to show up as unsaveable rows in the table.
    const outsideBlock = imported.filter(
      (slot) =>
        (!!blockStart && compareVacationDates(slot.startDate, blockStart) < 0) ||
        (!!blockEnd && compareVacationDates(slot.startDate, blockEnd) > 0)
    ).length;
    if (outsideBlock > 0) {
      lines.push(
        `${outsideBlock} block${outsideBlock === 1 ? " starts" : "s start"} outside this package's bid period and cannot be saved until corrected.`
      );
    }
    return lines;
  }, [csvImport, working.length, slots, slotsProtected, blockStart, blockEnd]);

  const handleOpenGenerate = useCallback(() => setGenerateOpen(true), []);
  const handleCloseGenerate = useCallback(() => setGenerateOpen(false), []);

  const handleDiscard = useCallback(() => setWorking(slots), [slots]);

  const handleSave = useCallback(async (): Promise<void> => {
    if (!packageId) return;
    setIsSaving(true);
    try {
      const notifications: INotifications = {
        dispatch,
        successMessage: "Vacation blocks saved",
        errorMessage: "Unable to save vacation blocks",
      };
      const response: IVacationBidPackageResponse | null = await saveVacationBidSlots(
        packageId,
        working,
        notifications
      );
      if (response?.vacationBidPackage) {
        onSlotsSaved(response.vacationBidPackage);
        // Changing which blocks are offered can change how entitlements divide, and so crew members' awards.
        const adjustments = awardAdjustmentsLine(response.awardAdjustments);
        if (adjustments) {
          dispatch(
            openSnackbar(
              awardChangesSnackbar(["Vacation blocks saved.", adjustments], !!response.awardAdjustments?.flagged)
            )
          );
        }
      }
    } finally {
      // The tab stays behind its spinner unless this runs on the throwing path too.
      setIsSaving(false);
    }
  }, [dispatch, packageId, working, onSlotsSaved]);

  const columns = useMemo<GridColDef<ISlotRow>[]>(
    () => [
      editableBooleanColumn<ISlotRow>("include", "Include", { flex: 6, alwaysVisibleCheckbox: true }),
      readOnlyTextColumn<ISlotRow>("slotId", "Slot ID", {
        flex: 9,
        valueGetter: (params: GridValueGetterParams<ISlotRow>) => displaySlotId(params.row.slotId, showSlotIdYear),
      }),
      editableIsoDateColumn<ISlotRow>("startDate", "Start Date", { flex: 10 }),
      editableIsoDateColumn<ISlotRow>("endDate", "End Date", { flex: 10 }),
      editableIntegerColumn<ISlotRow>("capacity", "Capacity", { min: 0, flex: 7 }),
      dataCol<ISlotRow>({
        field: ACTIONS_FIELD,
        headerName: "",
        flex: 5,
        filterable: false,
        renderCell: (params: GridRenderCellParams<ISlotRow>): JSX.Element => {
          const locked = isLockedRow(params.row);
          return (
            <Tooltip title={locked ? LOCKED_REMOVE_TOOLTIP : "Remove block"} placement="top">
              <span>
                <IconButton
                  size="small"
                  aria-label="Remove block"
                  disabled={locked}
                  onClick={() => handleDeleteRow(params.row.rowKey)}
                >
                  <DeleteIcon fontSize="inherit" />
                </IconButton>
              </span>
            </Tooltip>
          );
        },
      }),
    ],
    [showSlotIdYear, isLockedRow, handleDeleteRow]
  );

  return (
    <Box className="vacation_slots_tab" sx={(theme) => VacationSlotsTabStyles(theme).root}>
      <SpinnerProgress className="slotsSavingSpinner" loading={isSaving} message="saving vacation blocks" />

      <div className="slots_toolbar">
        <Button
          size="small"
          variant="outlined"
          startIcon={<AddIcon />}
          disabled={isSaving}
          onClick={handleAddSlot}
          data-testid={VACATION_BID_TEST_ID.addSlotButton}
        >
          Add Slot
        </Button>
        <Button
          size="small"
          variant="outlined"
          disabled={!packageId || isSaving}
          onClick={handleOpenGenerate}
          data-testid={VACATION_BID_TEST_ID.generateSlotsButton}
        >
          Generate Slots
        </Button>
        <Button
          size="small"
          variant="outlined"
          disabled={isSaving}
          onClick={handleOpenImport}
          data-testid={VACATION_BID_TEST_ID.importSlotsCsvButton}
        >
          Import Slots (CSV)
        </Button>
        <Tooltip title="Downloads the blocks shown here, including unsaved edits" placement="top">
          <span>
            <Button
              size="small"
              variant="outlined"
              disabled={!working.length || isSaving}
              onClick={handleExport}
              data-testid={VACATION_BID_TEST_ID.exportSlotsCsvButton}
            >
              Export Slots (CSV)
            </Button>
          </span>
        </Tooltip>
        <input
          type="file"
          accept=".csv,text/csv"
          hidden
          ref={fileInputRef}
          onChange={(event) => {
            void handleFileChosen(event);
          }}
          data-testid={VACATION_BID_TEST_ID.importSlotsCsvInput}
        />
        <div className="slots_toolbar_spacer" />
        <Button
          size="small"
          variant="contained"
          color="secondary"
          disabled={!slotsDirty || isSaving}
          onClick={handleDiscard}
          data-testid={VACATION_BID_TEST_ID.discardSlotsButton}
        >
          Discard
        </Button>
        <Button
          size="small"
          variant="contained"
          color="primary"
          disabled={!slotsDirty || !packageId || hasInvalidCells || isSaving}
          onClick={() => {
            void handleSave();
          }}
          data-testid={VACATION_BID_TEST_ID.saveSlotsButton}
        >
          Save Blocks
        </Button>
      </div>

      <Typography className="slots_caption" variant="body2">
        {`${working.length} blocks · ${includedCount} included`}
        {slotsProtected && (
          <span className="protected_note">
            {
              " · the bid window has opened: existing blocks cannot be removed or re-dated, but Include and Capacity can still change"
            }
          </span>
        )}
      </Typography>

      <Box ref={gridContainerRef} className="slots_grid_container">
        <EditableDataGrid<ISlotRow>
          data-testid={VACATION_BID_TEST_ID.slotsGrid}
          rowTestId={VACATION_BID_TEST_ID.slotRow}
          rows={gridRows}
          columns={columns}
          getRowId={getSlotRowId}
          onRowsChange={handleRowsChange}
          isCellDirty={isCellDirty}
          isCellInvalid={isCellInvalid}
          isCellEditable={isCellEditable}
          paginationModel={paginationModel}
          onPaginationModelChange={onPaginationModelChange}
          disableRowSelectionOnClick={true}
          loading={isSaving}
        />
      </Box>

      <GenerateSlotsDialog
        open={generateOpen}
        onClose={handleCloseGenerate}
        packageId={packageId}
        bidBlockStart={bidBlockStart}
        slotGeneration={lastGeneration}
        onGenerated={handleGenerated}
      />

      <ImportCsvDialog
        open={!!csvImport}
        title="Import Vacation Blocks"
        fileName={csvImport?.fileName ?? ""}
        summary={importSummary}
        issues={csvImport?.result.issues ?? []}
        fatal={csvImport?.result.fatal}
        confirmLabel="Import Blocks"
        confirmDisabled={!csvImport?.result.rows.length}
        onConfirm={handleConfirmImport}
        onClose={handleCloseImport}
      />
    </Box>
  );
};

export default VacationSlotsTab;
